import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LegacyTimeCountdown,
  legacyExtraWaitPrice,
  legacyPendingArrivedAt,
  legacyThousands,
  type LegacyOrder,
} from '@/entities/legacy-order';

/**
 * The waiting-fee column, a port of the old `WaitPriceInfo` in its
 * `withoutInfoBox` variant (`single/PriceFormula.tsx:77-104`).
 *
 * Two live pieces:
 * - the accrued fee = `wait_price` + the extra accruing at stage 8 for
 *   scooters (equipment 1) — recomputed on a timer tick so the figure climbs
 *   while the courier waits, like the old cell's own interval;
 * - at stage 8 a `TimeCountDown` (onlyText) against the pending pickup's
 *   `arrived_at`, wrapped in a tooltip, exactly as the old cell rendered it.
 *
 * LAYOUT is the old cell's own structure verbatim: the timer FIRST in the
 * DOM, the price second, inside a `column-reverse` flex — reproduced 1-to-1
 * rather than re-derived, so whichever way the old markup stacked them, this
 * stacks them the same. The price is the old `medium_14` black.
 */
export function LegacyWaitPriceCell({ order }: { order: LegacyOrder }) {
  const { t } = useTranslation('legacy');

  /** The fee only climbs at stage 8 on a scooter — no ticking otherwise. */
  const accruing = order.stage === 8 && order.equipment_id === 1;

  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    if (!accruing) return;
    // The old cell re-derived the fee once per accrued minute; a 30s tick
    // keeps the displayed figure within half a minute of that without the
    // old code's boundary-alignment machinery.
    const interval = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(interval);
  }, [accruing]);

  const extra = legacyExtraWaitPrice(
    {
      stage: order.stage,
      equipmentId: order.equipment_id,
      freeWaitTime: order.free_wait_time,
      pricePerMinuteAfterWait: order.price_per_minute_after_wait,
      origin: order.origin,
    },
    now,
  );

  const total = order.wait_price + extra;
  const arrivedAt = legacyPendingArrivedAt(order.origin);

  return (
    <div className="flex flex-col-reverse items-start justify-center gap-1">
      {order.stage === 8 && arrivedAt ? (
        <span title={t('tooltip.waiting_time')}>
          <LegacyTimeCountdown
            onlyText
            endTime={arrivedAt}
            differenceInMinutes={order.free_wait_time || 15}
          />
        </span>
      ) : null}
      <span className="text-[14px] font-medium text-[var(--lg-fg)]">
        {legacyThousands(total)} {t('uzs')}
      </span>
    </div>
  );
}
